import React, {useEffect, useMemo, useRef, useState} from "react";
import DataTable from "react-data-table-component";
import {customStyles} from "../SuperAdmin/utils";
import EmptyTableState from "../../utils/EmptyTableState";
import {isFt920} from "../../utils/deviceStatus";
import {ROLE_LABELS, STATUS_LABELS, TYPE_LABELS, PAGE_SIZES, deviceMix, timeAgo} from "./consoleModel";
import {AttentionPill, ClientStatusPill, OnlineBar, OnlinePill, Pill} from "./ConsoleParts";

const fmt = (n) => (n == null ? "–" : Number(n).toLocaleString("en-IN"));

// The table library only treats a click as a row click when the clicked element carries
// data-tag="allowRowEvents"; custom cell content doesn't, so every cell of a clickable table is
// wrapped in one that does (its children ignore the pointer, see superAdminConsole.css).
const clickableColumns = (cols) =>
  cols.map((c) => ({
    ...c,
    cell: (row) => (
      <div data-tag="allowRowEvents" className="sac-cell">
        {c.cell(row)}
      </div>
    ),
  }));

/**
 * DataTable with paging done on the server. page is 0-based. The table returns to page 1 whenever
 * resetKey changes (a filter changed), and opens on the URL's page on first render.
 */
function PagedTable({columns, rows, total, page, size, loading, resetKey, empty, onRowClicked, onPage}) {
  const [resetToggle, setResetToggle] = useState(false);
  const firstKey = useRef(resetKey);
  useEffect(() => {
    if (resetKey !== firstKey.current) {
      firstKey.current = resetKey;
      setResetToggle((t) => !t);
    }
  }, [resetKey]);
  return (
    <DataTable
      columns={columns}
      data={rows}
      keyField="__key"
      customStyles={customStyles}
      progressPending={loading && rows.length === 0}
      pagination
      paginationServer
      paginationTotalRows={total}
      paginationPerPage={size}
      paginationDefaultPage={page + 1}
      paginationResetDefaultPage={resetToggle}
      paginationRowsPerPageOptions={PAGE_SIZES}
      onChangePage={(p) => onPage({page: p - 1})}
      onChangeRowsPerPage={(newSize) => onPage({size: newSize, page: 0})}
      onRowClicked={onRowClicked}
      highlightOnHover
      pointerOnHover={!!onRowClicked}
      noDataComponent={<EmptyTableState message={empty} />}
    />
  );
}

const Avatar = ({name}) => (
  <span className="h-8 w-8 shrink-0 rounded-lg bg-brand-50 text-brand-700 grid place-items-center font-bold" aria-hidden="true">
    {(name || "?").charAt(0).toUpperCase()}
  </span>
);

export function ClientsTable({rows, total, filters, loading, resetKey, onOpen, onPage}) {
  const columns = useMemo(
    () =>
      clickableColumns([
      {
        name: "Client",
        grow: 2,
        cell: (r) => (
          <div className="flex items-center gap-2.5 py-2 min-w-0">
            <Avatar name={r.client.name} />
            <div className="min-w-0">
              <div className="flex items-center gap-2 font-semibold text-ink-900">
                <span className="truncate">{r.client.name}</span>
                {r.client.status !== "ACTIVE" && <ClientStatusPill status={r.client.status} />}
              </div>
              <div className="text-xs text-ink-500 truncate">
                {[r.client.city, r.client.contactName].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
        ),
      },
      {
        name: "Devices",
        grow: 2,
        cell: (r) => (
          <div className="py-2 min-w-0">
            <div className="font-semibold text-ink-900 tabular-nums">{fmt(r.devices)}</div>
            <div className="text-xs text-ink-500">{deviceMix(r.devicesByType) || "No devices"}</div>
          </div>
        ),
      },
      {name: "Online", cell: (r) => <OnlineBar row={r} />},
      {name: "Last heard", cell: (r) => timeAgo(r.lastContactUtc)},
      {name: "Attention", cell: (r) => <AttentionPill code={r.attention} />},
      ]),
    []
  );
  const data = useMemo(() => rows.map((r) => ({...r, __key: r.client.clientId})), [rows]);
  return (
    <PagedTable
      columns={columns}
      rows={data}
      total={total}
      page={filters.page}
      size={filters.size}
      loading={loading}
      resetKey={resetKey}
      empty="No clients match these filters"
      onRowClicked={(r) => onOpen(r.client.clientId)}
      onPage={onPage}
    />
  );
}

const statusCell = (d) =>
  d.assetStatus === "SUSPENDED" ? <Pill tone="warn">Suspended</Pill> : STATUS_LABELS[d.assetStatus] || d.assetStatus;

/** All devices (withClient) or one client's devices. */
export function DevicesTable({rows, total, filters, loading, resetKey, withClient, onOpen, onPage}) {
  const columns = useMemo(() => {
    const cols = [
      {
        name: "Device",
        grow: 2,
        cell: (d) => (
          <div className="py-2 min-w-0">
            <div className="font-semibold text-ink-900 truncate">{d.name}</div>
            <div className="text-xs text-ink-500 truncate">
              {TYPE_LABELS[d.assetType] || d.assetType}
              {isFt920(d) && (
                <span className="ml-1 px-1 rounded bg-info-50 text-info-700 text-[10px] font-bold">FT920</span>
              )}
              {" · "}
              <span className="font-mono">{d.unitId}</span>
            </div>
          </div>
        ),
      },
    ];
    if (withClient) {
      cols.push({name: "Client", grow: 1.5, cell: (d) => <span className="font-medium text-ink-800">{d.clientName}</span>});
    }
    cols.push(
      {name: "Status", cell: statusCell},
      {name: "Online", cell: (d) => <OnlinePill device={d} />},
      {name: "Last contact", cell: (d) => timeAgo(d.lastContactUtc)}
    );
    if (!withClient) {
      cols.push({
        name: "Assigned to",
        grow: 1.2,
        cell: (d) => d.assignedToName || <span className="text-ink-400">Unassigned</span>,
      });
    }
    return clickableColumns(cols);
  }, [withClient]);
  const data = useMemo(() => rows.map((d) => ({...d, __key: d.assetId})), [rows]);
  return (
    <PagedTable
      columns={columns}
      rows={data}
      total={total}
      page={filters.page}
      size={filters.size}
      loading={loading}
      resetKey={resetKey}
      empty="No devices match these filters"
      onRowClicked={(d) => onOpen(d)}
      onPage={onPage}
    />
  );
}

export function UsersTable({rows, total, filters, loading, resetKey, onPage}) {
  const columns = useMemo(
    () => [
      {name: "Name", grow: 1.5, cell: (u) => <span className="font-semibold text-ink-900">{u.fullName || u.username}</span>},
      {name: "Role", cell: (u) => ROLE_LABELS[u.userRole] || u.userRole},
      {
        name: "Sign-in",
        grow: 1.5,
        cell: (u) =>
          u.userRole === "MOBI_TRACK" ? (
            <span className="text-ink-600">Phone {u.countryCode} {u.phoneNumber}, WhatsApp code</span>
          ) : (
            <span className="font-mono text-xs text-ink-700">{u.email || u.username}</span>
          ),
      },
      {
        name: "Status",
        cell: (u) => (u.userStatus === "ACTIVE" ? <Pill tone="on">Active</Pill> : <Pill tone="warn">Suspended</Pill>),
      },
      {name: "Last sign-in", cell: (u) => timeAgo(u.lastLoginAt)},
    ],
    []
  );
  const data = useMemo(() => rows.map((u) => ({...u, __key: u.userId})), [rows]);
  return (
    <PagedTable
      columns={columns}
      rows={data}
      total={total}
      page={filters.page}
      size={filters.size}
      loading={loading}
      resetKey={resetKey}
      empty="No users match these filters"
      onPage={onPage}
    />
  );
}
